import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet } from 'react-native-web';
import Card from '../components/Card';
import Button from '../components/Button';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import Input from '../components/Input';
import PageHeader from '../components/PageHeader';
import { colors } from '../theme';
import { getPixels, createPixel, updatePixel, togglePixel, deletePixel } from '../api';

export default function PixelsPage() {
  const [pixels, setPixels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPixel, setEditingPixel] = useState(null);
  const [form, setForm] = useState({ pixelId: '', name: '', accessToken: '' });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getPixels();
      setPixels(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (editingPixel) {
      await updatePixel(editingPixel.id, form);
    } else {
      await createPixel(form);
    }
    setForm({ pixelId: '', name: '', accessToken: '' });
    setEditingPixel(null);
    setModalVisible(false);
    load();
  };

  const openCreateModal = () => {
    setEditingPixel(null);
    setForm({ pixelId: '', name: '', accessToken: '' });
    setModalVisible(true);
  };

  const openEditModal = (p) => {
    setEditingPixel(p);
    setForm({
      pixelId: p.pixelId,
      name: p.name,
      accessToken: '', // don't prefill token for security
    });
    setModalVisible(true);
  };

  const handleToggle = async (id) => {
    await togglePixel(id);
    load();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this pixel? This will also remove all mappings.')) {
      await deletePixel(id);
      load();
    }
  };

  return (
    <View>
      <PageHeader title="Pixels" subtitle="Manage your Facebook Pixels and Conversions API tokens">
        <Button label="+ New Pixel" onPress={openCreateModal} />
      </PageHeader>

      <View style={styles.grid}>
        {pixels.map((p) => (
          <Card key={p.id} style={styles.pixelCard}>
            <View style={styles.cardHeader}>
              <View style={styles.pixelIcon}>
                <Text style={styles.pixelIconText}>🎯</Text>
              </View>
              <View style={styles.cardInfo}>
                <Text style={styles.cardTitle}>{p.name}</Text>
                <Text style={styles.pixelIdText}>ID: {p.pixelId}</Text>
              </View>
            </View>

            <View style={styles.statusRow}>
              <Badge label={p.active ? 'Active' : 'Disabled'} color={p.active ? 'success' : 'muted'} />
              <Badge label={p.hasAccessToken ? 'CAPI Ready' : 'No Token'} color={p.hasAccessToken ? 'primary' : 'warning'} />
            </View>

            <View style={styles.cardActions}>
              <Button label="Edit" variant="ghost" size="sm" onPress={() => openEditModal(p)} />
              <Button
                label={p.active ? 'Disable' : 'Enable'}
                variant="ghost"
                size="sm"
                onPress={() => handleToggle(p.id)}
              />
              <Button label="Delete" variant="ghost" size="sm" onPress={() => handleDelete(p.id)} />
            </View>
          </Card>
        ))}
      </View>

      {!loading && pixels.length === 0 && (
        <Card>
          <View style={styles.empty}>
            <Text style={styles.emptyIcon}>🎯</Text>
            <Text style={styles.emptyTitle}>No pixels yet</Text>
            <Text style={styles.emptyDesc}>Add your Facebook Pixels to start receiving events</Text>
            <Button label="Add Pixel" onPress={openCreateModal} style={{ marginTop: 16 }} />
          </View>
        </Card>
      )}

      <Modal
        visible={modalVisible}
        title={editingPixel ? 'Edit Pixel' : 'New Pixel'}
        onClose={() => { setModalVisible(false); setEditingPixel(null); }}
      >
        <Input label="Facebook Pixel ID" value={form.pixelId} onChangeText={(v) => setForm({ ...form, pixelId: v })} placeholder="123456789012345" />
        <Input label="Name" value={form.name} onChangeText={(v) => setForm({ ...form, name: v })} placeholder="Main Pixel / Backup Pixel" />
        <Input
          label={editingPixel ? 'CAPI Access Token (enter new to update)' : 'CAPI Access Token'}
          value={form.accessToken}
          onChangeText={(v) => setForm({ ...form, accessToken: v })}
          placeholder={editingPixel ? 'Leave empty to keep current token' : 'EAAxxxxx...'}
          secureTextEntry
        />
        <Text style={styles.tokenHint}>Required for Conversions API. Get it from Facebook Events Manager → Settings.</Text>
        <Button label={editingPixel ? 'Save Changes' : 'Create'} onPress={handleSave} style={{ marginTop: 8 }} />
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  pixelCard: {
    width: 340,
    minWidth: 300,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  pixelIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: 'rgba(99,102,241,0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  pixelIconText: {
    fontSize: 20,
  },
  cardInfo: {
    flex: 1,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.white,
  },
  pixelIdText: {
    fontSize: 12,
    color: colors.textMuted,
    fontFamily: 'monospace',
    marginTop: 2,
  },
  statusRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
  },
  cardActions: {
    flexDirection: 'row',
    gap: 8,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  empty: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: 12,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 4,
  },
  emptyDesc: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  tokenHint: {
    fontSize: 12,
    color: colors.textMuted,
    marginTop: 4,
  },
});
